import React, { useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    Divider,
    Group,
    Modal,
    Stack,
    Text,
    useMantineTheme,
} from "@mantine/core";

export type ResponsibilityAgreementModalProps = {
    titleText: string;
    promptText: string;
    buttonText: string;
    opened: boolean;
    onClose: () => void;
    onConfirm: () => void;
    statements: AgreementStatement[];
};

export type AgreementStatement = {
    key: string;
    label: React.ReactNode;
};

const ResponsibilityAgreementModal: React.FC<ResponsibilityAgreementModalProps> = ({
    titleText,
    promptText,
    buttonText,
    opened,
    onClose,
    onConfirm,
    statements
}) => {
    const theme = useMantineTheme();

    const [checked, setChecked] = useState<boolean[]>([]);

    // Reset checkboxes each time the modal is opened or the set of statements changes
    useEffect(() => {
        if (opened) {
            setChecked(statements.map(() => false));
        }
    }, [opened, statements]);

    const allChecked = checked.length > 0 && checked.every(Boolean);

    const toggleIndex = (index: number) => {
        setChecked((prev) => prev.map((v, i) => (i === index ? !v : v)));
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={700}>{titleText}</Text>}
            centered
            size="lg"
        >
            <Stack gap="md">
                <Text c="dimmed" fz="sm">
                    {promptText}
                </Text>

                <Stack gap="sm">
                    {statements.map((statement, index) => (
                        <React.Fragment key={statement.key}>
                            <Divider variant="dashed"/>
                            <Checkbox
                                checked={checked[index] ?? false}
                                onChange={() => toggleIndex(index)}
                                label={statement.label}
                            />
                        </React.Fragment>
                    ))}
                </Stack>

                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        color={theme.colors.green[6]}
                        disabled={!allChecked}
                        onClick={onConfirm}
                    >
                        {buttonText}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ResponsibilityAgreementModal;
